/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";

const JWTExpiryDialog = ({
  open,
  remainingSeconds,
  isRefreshingToken,
  onContinue,
  onLogout,
}) => {
  return (
    <Dialog
      open={open}
      onClose={() => {}} // Prevent closing by clicking outside
      maxWidth="xs"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            padding: 1,
            margin: 2,
            minWidth: 320,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          fontSize: "18px",
          fontWeight: 600,
          color: "#d32f2f",
          padding: "16px 16px 8px 16px",
        }}
      >
        ⚠️ Session Expiring Soon
      </DialogTitle>
      
      <DialogContent sx={{ padding: "8px 16px" }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              marginBottom: 2,
              fontSize: "14px",
              color: "#333",
            }}
          >
            Your session will expire in:
          </Typography>
          
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 100,
              height: 70,
              borderRadius: 2,
              backgroundColor: remainingSeconds <= 10 ? "#ffebee" : "#e3f2fd",
              border: `3px solid ${remainingSeconds <= 10 ? "#d32f2f" : "#1976d2"}`,
              marginBottom: 2,
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: remainingSeconds <= 10 ? "#d32f2f" : "#1976d2",
                fontFamily: "monospace",
                fontSize: "28px",
              }}
            >
              {remainingSeconds}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: remainingSeconds <= 10 ? "#d32f2f" : "#1976d2",
                fontSize: "11px",
                fontWeight: 500,
              }}
            >
              seconds
            </Typography>
          </Box>
          
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              marginBottom: 1,
              fontSize: "13px",
            }}
          >
            Click "Continue" to extend your session or "Logout" to sign out.
          </Typography>
          
          {isRefreshingToken && (
            <Typography
              variant="body2"
              sx={{
                color: "#1976d2",
                fontStyle: "italic",
                fontSize: "12px",
                marginTop: 1,
              }}
            >
              Refreshing session...
            </Typography>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions
        sx={{
          justifyContent: "center",
          gap: 2,
          padding: "8px 16px 16px 16px",
        }}
      >
        <Button
          onClick={onLogout}
          variant="outlined"
          color="error"
          sx={{
            minWidth: 100,
            textTransform: "none",
            fontSize: "14px",
          }}
          disabled={isRefreshingToken}
        >
          Logout
        </Button>
        <Button
          onClick={onContinue}
          variant="contained"
          color="primary"
          sx={{
            minWidth: 100,
            textTransform: "none",
            fontSize: "14px",
          }}
          disabled={isRefreshingToken}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

JWTExpiryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  remainingSeconds: PropTypes.number.isRequired,
  isRefreshingToken: PropTypes.bool.isRequired,
  onContinue: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};

export default JWTExpiryDialog;
